/**
 * Stored data sources API (discover-api). Uses discoverRuns / PRIV_PLAT_API.
 * OpenAPI: /api/datasources, DataSourceRequestDTO (config), DataSourceTestResponseDTO (dataSourceId).
 */
import { API } from '../Auth';
import { isFileLikeDatasourceType } from '../Utils/fileTypeUtils';

const EP = 'discoverRuns';
const BASE = '/api/datasources';

/** Numeric id for API calls (from saved row or tableData). */
export function getDataSourceNumericId(row) {
  if (row == null) return null;
  const raw = row.id ?? row.tableData?.id;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** @deprecated use getDataSourceNumericId */
export const getConnectionNumericId = getDataSourceNumericId;

export function uiTypeToDatasourceType(type) {
  const t = (type || '').toLowerCase();
  if (t === 'mongodb' || t === 'dynamodb') return 'NOSQL';
  if (t === SAAS_SALESFORCE_TYPE) return 'SAAS';
  if (t === 's3' || t === 'sftp') return 'FILE_FOLDER';
  return 'RELATIONAL';
}

/**
 * UI-only sentinel that the type dropdown uses to identify a file source row in any
 * scheme. Mirrors the role of an engine string ("postgresql", "snowflake") for DB
 * sources, but is uniform across LOCAL/S3/AZURE_BLOB/SFTP — the platform is a sub-
 * discriminator carried on `vfsScheme`. Anything in the UI that needs to branch
 * "is this a file source?" should compare to this constant.
 */
export const FILE_SOURCE_TYPE = 'file';

/**
 * Engine string for a Salesforce org (datasourceType SAAS). Like the NoSQL engines it comes
 * from a static dropdown entry, not the JDBC driver registry. Config keys are the ones the
 * platform and both workers agree on (tdm-worker-integration.md): loginUrl, authFlow
 * (JWT_BEARER | CLIENT_CREDENTIALS), consumerKey, username, privateKeyPem | clientSecret,
 * apiVersion, audience, advanced.
 *
 * `audience` is the JWT `aud` claim and defaults to `loginUrl`; it replaced a `sandbox` boolean
 * that mapped the audience onto one of the two shared login hosts and could never name a My
 * Domain URL. `advanced` holds optional per-connection overrides of the worker's tuning knobs.
 */
/** Numeric `config.advanced` keys, coerced so the stored config holds numbers not strings. */
const SALESFORCE_ADVANCED_NUMERIC = new Set([
  'ingestChunkBytes', 'bisectMinRows', 'inChunkIds', 'wholeObjectThresholdKeys',
  'apiReserve', 'apiReservePercent', 'httpTimeoutSeconds', 'bulkPollMaxSeconds',
  'bulkJobTimeoutMinutes', 'queryBatchSize',
]);

/**
 * Shape `config.advanced` for the wire. Returns null when nothing is set, so the key is omitted
 * entirely — absent means "use the platform default", and the server deliberately does not write
 * defaults in, so a later change to one still reaches sources that never overrode it.
 * Non-numeric junk passes through for the server to reject by field name.
 */
export function buildSalesforceAdvanced(advanced) {
  if (!advanced || typeof advanced !== 'object') return null;
  const out = {};
  Object.keys(advanced).forEach((k) => {
    const raw = advanced[k];
    if (raw == null || String(raw).trim() === '') return;
    const v = String(raw).trim();
    if (SALESFORCE_ADVANCED_NUMERIC.has(k)) {
      const n = Number(v);
      out[k] = Number.isFinite(n) ? n : v;
    } else {
      out[k] = v;
    }
  });
  return Object.keys(out).length ? out : null;
}

export const SAAS_SALESFORCE_TYPE = 'salesforce';
export const SALESFORCE_LOGIN_URL_PRODUCTION = 'https://login.salesforce.com';
export const SALESFORCE_LOGIN_URL_SANDBOX = 'https://test.salesforce.com';
export const SALESFORCE_DEFAULT_API_VERSION = 'v62.0';

export function isSaasType(type) {
  return String(type || '').toLowerCase() === SAAS_SALESFORCE_TYPE;
}

/** True for a Salesforce row by either signal a stored connection carries. */
export function isSaasSource(connection) {
  if (!connection) return false;
  return connection.datasourceType === 'SAAS' || isSaasType(connection.type);
}

/**
 * Map a file VFS scheme to the dropdown option value the type selector uses
 * (file_local / file_s3 / file_azure / file_sftp). The selector binds against
 * `customDatabaseType` for both DB drivers and file platforms, so the editor can
 * resolve a saved file row back to its row in the option list on edit.
 */
export function vfsSchemeToTypeOption(scheme) {
  switch (scheme) {
    case 'S3': return 'file_s3';
    case 'AZURE_BLOB': return 'file_azure';
    case 'SFTP': return 'file_sftp';
    case 'SHAREPOINT': return 'file_sharepoint';
    case 'LOCAL':
    default: return 'file_local';
  }
}

// The canonical file-type membership lives in Utils/fileTypeUtils (single source of truth,
// kept in sync with the worker enum). `isFileDatasourceType` is the lenient variant — it
// includes the legacy FILE_FOLDER orphan, which these callers historically treated as a
// file source. Re-exported under the local name so existing call sites are unchanged.
export function isFileDatasourceType(dst) {
  return typeof dst === 'string' && isFileLikeDatasourceType(dst);
}

// Loopback aliases all resolve to the same box. A row saying `localhost` and another
// saying `host.docker.internal` are the same physical host from the platform's point of
// view, so they fold into one bucket before we compare host identity.
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', 'host.docker.internal']);
const LOOPBACK_BUCKET = '__loopback__';

// Default listening port per engine, used when a row leaves `port` blank (the connection
// still targets the engine default). Keyed on the lowercased engine string carried in
// `type`. Engines not listed here have no assumed default — a blank port stays unknown.
export const ENGINE_DEFAULT_PORTS = {
  postgresql: 5432,
  mysql: 3306,
  mariadb: 3306,
  sqlserver: 1433,
  oracle: 1521,
  db2: 50000,
};

function normalizeHostBucket(host) {
  if (host == null) return '';
  const h = String(host).trim().toLowerCase();
  if (h === '') return '';
  return LOOPBACK_HOSTS.has(h) ? LOOPBACK_BUCKET : h;
}

export function effectivePort(row) {
  const raw = row && row.port;
  if (raw != null && String(raw).trim() !== '') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  const engine = String((row && row.type) || '').toLowerCase();
  return Object.prototype.hasOwnProperty.call(ENGINE_DEFAULT_PORTS, engine)
    ? ENGINE_DEFAULT_PORTS[engine]
    : null;
}

// The normalized JDBC URL for rows driven off a single URL string, else null. Two shapes
// carry a URL: the explicit `jdbcUrl` field, and legacy `type === 'jdbc'` rows that stash
// the URL in `host` (see toUiDataSource). Field-based rows return null and are compared by
// host/port/database instead.
function jdbcUrlOf(row) {
  if (!row) return null;
  if (row.jdbcUrl != null && String(row.jdbcUrl).trim() !== '') {
    return String(row.jdbcUrl).trim().toLowerCase();
  }
  if (String(row.type || '').toLowerCase() === 'jdbc' && row.host != null && String(row.host).trim() !== '') {
    return String(row.host).trim().toLowerCase();
  }
  return null;
}

/**
 * True when two data source rows point at the same physical database. Schema-blind by
 * design — two rows on the same database with different schemas are a legitimate
 * source/target pair. Combined with schema comparison in isEligibleMaskTarget /
 * isSameMaskDestination to spot duplicate rows that are the same masking destination.
 *
 * Identity is database-level, deliberately ignoring schema:
 *   - Hosts are trimmed/lowercased and loopback aliases fold together.
 *   - Port falls back to the engine default when blank.
 *   - JDBC-URL-mode rows compare by normalized URL string.
 *   - Same host + port with the database missing on either side is treated as SAME
 *     (conservative — a hidden row is still reachable via REST); different databases on
 *     the same server are NOT same (dev/test DBs legitimately share a server).
 */
export function isSamePhysicalDatabase(a, b) {
  if (!a || !b) return false;

  // JDBC-URL-mode rows are only comparable to other URL-mode rows: equal normalized URLs
  // => same. A URL-mode row vs. a field-based row can't be asserted equal, so treat as not
  // same (the safe direction — we err toward NOT hiding).
  const urlA = jdbcUrlOf(a);
  const urlB = jdbcUrlOf(b);
  if (urlA || urlB) {
    return urlA != null && urlB != null && urlA === urlB;
  }

  const hostA = normalizeHostBucket(a.host);
  const hostB = normalizeHostBucket(b.host);
  if (hostA === '' || hostB === '') return false; // no host to compare — not same
  if (hostA !== hostB) return false;

  const portA = effectivePort(a);
  const portB = effectivePort(b);
  if (portA == null || portB == null || portA !== portB) return false;

  const dbA = a.database != null ? String(a.database).trim().toLowerCase() : '';
  const dbB = b.database != null ? String(b.database).trim().toLowerCase() : '';
  if (dbA === '' || dbB === '') return true; // missing db on a shared host+port — conservative
  return dbA === dbB;
}

/**
 * True when `candidate` may receive masked data from `source` (cross-source masking
 * destination). Single source of truth for the InventoryPage target dropdown, the
 * Masking Runs wizard, and the group add-step dialog:
 *   writable DB row, not the source itself (by row id only — two rows on the same host,
 *   or even the same database with different schemas, are a legitimate source/target
 *   pair), and the same engine as the source (the INSERT path replays source-shaped
 *   DDL/DML). The same-engine rule is also what scopes mongodb masking to
 *   mongodb→mongodb: a mongo candidate is eligible only for a mongo source, and a mongo
 *   source is never offered non-mongo targets.
 */
export function isEligibleMaskTarget(source, candidate) {
  if (!source || !candidate || candidate.id == null) return false;
  if (candidate.id === source.id) return false;
  if (candidate.readOnly) return false;
  if (isPlatformManaged(candidate)) return false;
  if (candidate.type === FILE_SOURCE_TYPE || isFileDatasourceType(candidate.datasourceType)) return false;
  // no NoSQL masking for dynamo yet; platform rejects with ENGINE_NOT_MASKABLE. Without
  // this the same-engine rule below would offer dynamodb→dynamodb like mongodb→mongodb.
  if (String(source.type || '').toLowerCase() === 'dynamodb'
    || String(candidate.type || '').toLowerCase() === 'dynamodb') return false;
  // Salesforce is profile + discover only in v1 (platform NON_MASKABLE_ENGINES); in-org masking
  // is a follow-on, and a Salesforce org is never a target for another engine's rows.
  if (isSaasType(source.type) || isSaasType(candidate.type)
    || source.datasourceType === 'SAAS' || candidate.datasourceType === 'SAAS') return false;
  if (String(candidate.type || '').toLowerCase() !== String(source.type || '').toLowerCase()) return false;
  // A DIFFERENT row that resolves to the source's own database and schema (a duplicate
  // data-source row — a legal state) is the source under another name: masking to it
  // truncates the data being read. Same database with a DISTINCT schema stays eligible
  // (multi-tenant fan-out). Mirrors the server-side DataSourceEndpoint guard, which
  // also rejects a same-database target with no schema of its own.
  if (isSamePhysicalDatabase(source, candidate)) {
    const candidateSchema = schemaOf(candidate);
    if (candidateSchema === '' || candidateSchema === schemaOf(source)) return false;
  }
  return true;
}

const schemaOf = (row) => {
  const s = row && (row.schemaName != null ? row.schemaName : row.schema);
  return s == null ? '' : String(s).trim().toLowerCase();
};

/**
 * True when two eligible target rows are the same masking DESTINATION — same physical
 * database and same schema (both blank counts as same: both would inherit the same
 * default qualification). Used to fold duplicate data-source rows out of fan-out
 * destination lists: one fan-out job must never write the same destination twice.
 */
export function isSameMaskDestination(a, b) {
  if (!isSamePhysicalDatabase(a, b)) return false;
  return schemaOf(a) === schemaOf(b);
}

/**
 * The saved row already DEDICATED to `schemaName` on the same physical database (and
 * engine) as `source` — `source` itself included when its own schema matches — or null.
 * Drives the per-schema action on test-connection results: schemas with a dedicated row
 * render a link to it instead of a "create source" button. A same-database row with a
 * blank schema (like the parent source that surfaced the schema list) does NOT count
 * as dedicated.
 */
export function findDedicatedSchemaSource(source, schemaName, connections) {
  const wanted = schemaName == null ? '' : String(schemaName).trim().toLowerCase();
  if (!source || wanted === '') return null;
  const engine = String(source.type || '').toLowerCase();
  return (connections || []).find((row) =>
    row
    && String(row.type || '').toLowerCase() === engine
    && isSamePhysicalDatabase(source, row)
    && schemaOf(row) === wanted) || null;
}

export function hasDedicatedSchemaSource(source, schemaName, connections) {
  return findDedicatedSchemaSource(source, schemaName, connections) != null;
}

/**
 * Create a new data source dedicated to one schema of `parent`'s database. Clones the
 * parent's connection fields through the standard request DTO builder, overrides the
 * schema, and sends parentDataSourceId so the server inherits the parent's stored
 * password (DataSourceServiceImpl.inheritParentTopLevelSecretsIfRequested) — secrets
 * only ever leave the server as "****" placeholders, so the copy must happen there.
 * Returns the created API row.
 */
export async function createSchemaChildSource(parent, schemaName, name) {
  const dto = toRequestDto({
    ...parent,
    name,
    schema: undefined,
    schemaName,
    password: '',
    privateKey: '',
    readOnly: false,
  });
  dto.parentDataSourceId = Number(parent.id);
  return createDataSource(dto);
}

const omitEmpty = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null && String(v) !== ''));

// Extract { account, container } from an azblob://account/container/prefix rootPath.
// Returns empties for non-azblob or partial paths. Mirrors parseAzureBlob() server-side.
export const parseAzblobPath = (rootPath) => {
  const out = { account: undefined, container: undefined };
  if (!rootPath || typeof rootPath !== 'string') return out;
  const trimmed = rootPath.trim();
  if (!/^azblob:\/\//i.test(trimmed)) return out;
  const parts = trimmed.slice('azblob://'.length).split('/').filter(Boolean);
  if (parts.length >= 1) out.account = parts[0];
  if (parts.length >= 2) out.container = parts[1];
  return out;
};

/**
 * Reshape per-scheme flat fields off the connection into the worker-side
 * `vfsCredentials` map. Mirrors the per-scheme branches in the legacy
 * FileVfsConfigStep (which built the same map at scan time) so saved rows
 * round-trip 1:1 with what the wizard used to send.
 */
function buildFileVfsCredentials(c) {
  switch (c.vfsScheme) {
    case 'SFTP':
      return omitEmpty({
        host: c.vfsHost,
        port: c.vfsPort != null && c.vfsPort !== '' ? (Number(c.vfsPort) || c.vfsPort) : undefined,
        username: c.vfsUsername,
        password: isRedactedSecret(c.vfsPassword) ? undefined : c.vfsPassword,
      });
    case 'S3':
      return omitEmpty({
        region: c.region,
        accessKeyId: c.accessKeyId,
        secretAccessKey: isRedactedSecret(c.secretAccessKey) ? undefined : c.secretAccessKey,
        endpointUrl: c.endpointUrl,
      });
    case 'AZURE_BLOB': {
      // Backfill account/container from an azblob://account/container/ rootPath when the
      // explicit fields are empty (they are disabled in the editor once the rootPath carries
      // the prefix). FILE_GENERIC_FOLDER sources are validated server-side against
      // vfsCredentials.account/container only — not the rootPath — so without this a
      // discovered "azblob://acct/cont/" path would be rejected as "account and container
      // required". Mirrors parseAzureBlob() in DataSourceServiceImpl.
      const parsed = parseAzblobPath(c.rootPath);
      return omitEmpty({
        account: c.account || parsed.account,
        container: c.container || parsed.container,
        accountKey: isRedactedSecret(c.accountKey) ? undefined : c.accountKey,
        sasToken: isRedactedSecret(c.sasToken) ? undefined : c.sasToken,
        connectionString: isRedactedSecret(c.connectionString) ? undefined : c.connectionString,
        endpointUrl: c.endpointUrl,
      });
    }
    case 'SHAREPOINT':
      // App-only Entra credentials. tenantId + clientId always; exactly one proof —
      // clientSecret OR the certificate PEM pair (server-side validation enforces the
      // XOR; the editor's auth-method toggle clears the other side on switch).
      return omitEmpty({
        tenantId: c.tenantId,
        clientId: c.clientId,
        clientSecret: isRedactedSecret(c.clientSecret) ? undefined : c.clientSecret,
        certificatePem: c.certificatePem,
        certificatePrivateKey: isRedactedSecret(c.certificatePrivateKey) ? undefined : c.certificatePrivateKey,
        endpointUrl: c.endpointUrl,
      });
    case 'LOCAL':
    default:
      return {};
  }
}

function inferEngineFromDatasourceType(datasourceType, existingEngine) {
  if (existingEngine) return existingEngine;
  if (datasourceType === 'NOSQL') return 'mongodb';
  if (datasourceType === 'SAAS') return SAAS_SALESFORCE_TYPE;
  if (datasourceType === 'FILE_FOLDER') return 's3';
  return 'mysql';
}

/** API may return redacted secrets (e.g. "****"); never send those back or the real password is overwritten. */
function isRedactedSecret(value) {
  if (value == null || value === '') return false;
  const s = String(value).trim();
  return s.length > 0 && /^\*+$/.test(s);
}

// connectionProperties is persisted as a JSON object map ({ "key": "value", ... }) so the
// workers can bind it directly without agreeing on a delimiter. The editor textarea, however,
// stays line-oriented (one KEY=VALUE per line, '#' comments) because that's the natural way to
// type driver flags. These two helpers convert between the two shapes at the API boundary.

// Textarea text -> object map. One KEY=VALUE per line; first '=' splits key from value so
// values may contain '='. Blank lines and '#' comments are dropped. Returns null when there
// is nothing to store so the field is omitted from config entirely (matches prior behavior).
function connectionPropertiesTextToObject(text) {
  if (text == null || text === '') return null;
  const out = {};
  for (const raw of String(text).split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const eq = line.indexOf('=');
    if (eq <= 0) continue;
    const key = line.slice(0, eq).trim();
    const value = line.slice(eq + 1).trim();
    if (key) out[key] = value;
  }
  return Object.keys(out).length ? out : null;
}

// Stored value -> textarea text. Accepts the new object map and, for rows saved before this
// change, the legacy string forms (newline-separated from the old UI, or ';'/'&'-separated
// from a hand-edited masking config). Always renders back as one KEY=VALUE per line.
function connectionPropertiesToText(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([k, v]) => `${k}=${v}`)
      .join('\n');
  }
  // Legacy string: normalize any of \n, ';', '&' separators to one pair per line.
  return String(value)
    .split(/[\n;&]+/)
    .map((s) => s.trim())
    .filter((s) => s && !s.startsWith('#'))
    .join('\n');
}

function buildDataSourceConfig(row) {
  const c = row || {};
  const d = {};
  const isJdbc = (c.type || '').toLowerCase() === 'jdbc';
  // Free-text description of what this source is, shown under its name on the Data Source Risk
  // Profile. It rides in connection_details like every other editor field: the platform stores
  // that blob verbatim (the incoming config REPLACES the stored JSONB), so it round-trips
  // through toUiDataSource → the form → toRequestDto with no backend change, and travels with
  // the source through export/import.
  const description = typeof c.description === 'string' ? c.description.trim() : '';
  // DynamoDB is addressed by region, not host/port/database — its config keys are
  // region (required), optional accessKeyId/secretAccessKey (blank = the worker's ambient
  // AWS identity / IRSA), optional endpointUrl (DynamoDB Local), optional table (scope
  // discovery to one table). Return early so a mid-edit toggle from a DB type can never
  // ship stale host/port/database fields on a dynamo source.
  // Salesforce: no host/port/database at all — the org is addressed by loginUrl and an OAuth
  // flow. Only the ACTIVE flow's secret is sent (the platform drops the other anyway), and a
  // redacted "****" echo is never sent back. Return early for the same reason dynamo does.
  if (isSaasType(c.type)) {
    if (description) d.description = description;
    if (c.loginUrl != null && c.loginUrl !== '') d.loginUrl = String(c.loginUrl).trim();
    const authFlow = String(c.authFlow || 'JWT_BEARER').toUpperCase();
    d.authFlow = authFlow;
    if (c.consumerKey != null && c.consumerKey !== '') d.consumerKey = String(c.consumerKey).trim();
    if (c.apiVersion != null && c.apiVersion !== '') d.apiVersion = String(c.apiVersion).trim();
    if (c.audience != null && c.audience !== '') d.audience = String(c.audience).trim();
    const advanced = buildSalesforceAdvanced(c.advanced);
    if (advanced) d.advanced = advanced;
    // The owner's object choice from the profile page (object API names). Written whenever the
    // row carries it — including [] — so clearing the choice on the page clears it on the platform;
    // a row that never loaded it (older editor form) leaves the stored value alone.
    if (Array.isArray(c.selectedObjects)) d.selectedObjects = c.selectedObjects.map((o) => String(o).trim()).filter(Boolean);
    if (authFlow === 'CLIENT_CREDENTIALS') {
      if (c.clientSecret != null && c.clientSecret !== '' && !isRedactedSecret(c.clientSecret)) {
        d.clientSecret = c.clientSecret;
      }
    } else {
      const user = c.username != null ? c.username : c.user;
      if (user != null && user !== '') d.username = String(user).trim();
      if (c.privateKeyPem != null && c.privateKeyPem !== '' && !isRedactedSecret(c.privateKeyPem)) {
        d.privateKeyPem = c.privateKeyPem;
      }
    }
    return d;
  }
  if ((c.type || '').toLowerCase() === 'dynamodb') {
    if (description) d.description = description;
    if (c.region != null && c.region !== '') d.region = c.region;
    if (c.accessKeyId != null && c.accessKeyId !== '') d.accessKeyId = c.accessKeyId;
    if (c.secretAccessKey != null && c.secretAccessKey !== '' && !isRedactedSecret(c.secretAccessKey)) {
      d.secretAccessKey = c.secretAccessKey;
    }
    if (c.endpointUrl != null && c.endpointUrl !== '') d.endpointUrl = c.endpointUrl;
    if (c.table != null && c.table !== '') d.table = c.table;
    return d;
  }
  // _connectionMode === 'JDBC_URL' means the user explicitly chose to drive the connection
  // off a single JDBC URL string. Don't ship host/port/database/schema even if the form has
  // stale values from a previous toggle to FIELDS — otherwise the platform-api's resolver
  // would still see them, which is harmless today (top-level jdbcUrl wins) but makes future
  // debugging confusing and risks silent drift if precedence ever changes.
  const skipHostFields = c._connectionMode === 'JDBC_URL';
  if (!isJdbc && !skipHostFields && c.host != null && c.host !== '') d.host = c.host;
  if (!skipHostFields && c.port != null && c.port !== '') d.port = Number(c.port) || c.port;
  if (!skipHostFields && c.database != null && c.database !== '') d.database = c.database;
  const schema = c.schema != null ? c.schema : c.schemaName;
  if (!skipHostFields && schema != null && schema !== '') d.schema = schema;
  const user = c.username != null ? c.username : c.user;
  if (user != null && user !== '') d.username = user;
  if (c.password != null && c.password !== '' && !isRedactedSecret(c.password)) d.password = c.password;
  if (!skipHostFields && c.warehouse != null && c.warehouse !== '') d.warehouse = c.warehouse;
  if (c.path != null && c.path !== '') d.path = c.path;
  if (c.privateKey != null && c.privateKey !== '' && !isRedactedSecret(c.privateKey)) d.privateKey = c.privateKey;
  if (c.protocol != null && c.protocol !== '') d.protocol = c.protocol;
  if (c.peering != null && c.peering !== '') d.peering = c.peering;
  // AWS_IAM region. Stored inside connection_details (config), not top-level — the
  // platform-api reads it back out of the config blob and forwards it to the worker
  // (DataSourceTestServiceImpl). authMode/cloudAccountId, by contrast, are top-level
  // on the request DTO and are added in toRequestDto, not here.
  if (c.region != null && c.region !== '') d.region = c.region;
  // SQL Server-only TLS toggles. Persist explicit booleans so the API/worker URL builders can
  // tell "user said false" apart from "field was never set" (which falls back to MS JDBC v10+
  // defaults: encrypt=true, trustServerCertificate=false).
  if (typeof c.encrypt === 'boolean') d.encrypt = c.encrypt;
  if (typeof c.trustServerCertificate === 'boolean') d.trustServerCertificate = c.trustServerCertificate;
  // Per-connection JDBC properties: the editor holds free-form KEY=VALUE text (one per line);
  // persist it as a JSON object map so the workers bind it without delimiter guessing. The
  // discover-api / masking-service merge it into the JDBC properties at connect time; auth
  // wins on conflict.
  const cpObject = connectionPropertiesTextToObject(c.connectionProperties);
  if (cpObject) {
    d.connectionProperties = cpObject;
  }
  // customDatabaseType: discriminator the discover-api uses to pick the registered driver row.
  if (c.customDatabaseType != null && c.customDatabaseType !== '') {
    d.customDatabaseType = c.customDatabaseType;
  }
  if (description) d.description = description;
  return d;
}

/**
 * Body for POST/PUT /api/datasources (no schedule / Glue fields).
 */
/**
 * Preferred discover provider id for the request DTO. Only included when present so a partial
 * update preserves the existing value (backend treats null as "no change"). An explicit empty
 * string clears the preference; the editor sends '' to "Use default". Discovery is the only
 * job family whose provider lives on the data source — masking/encrypt preferences moved onto
 * each inventory (DataSourceRequestDTO no longer has those fields).
 */
function preferredProvidersDto(c) {
  const out = {};
  if (c.preferredDiscoverProviderId !== undefined) out.preferredDiscoverProviderId = c.preferredDiscoverProviderId || '';
  return out;
}

/**
 * Exclusion filter pre-filled into a NEW file data source.
 *
 * Written as a comma-separated GLOB LIST rather than a regex, which is both what users
 * reach for unprompted and what makes the seeded value self-documenting: a glance at the
 * field says what is being skipped, and removing one entry is a text edit instead of
 * surgery on an alternation. The worker accepts either form (`FileNameGlobs` in
 * c2-discover-api translates a list like this to one anchored regex), so a user who wants
 * regex precision can still paste one in.
 *
 * Only lists file types discovery cannot extract PII from today, so deleting an entry
 * changes cost, not coverage:
 *   - build artifacts / compiled binaries (jar war ear class dll so pdb exe msi)
 *   - DB backups and dumps (bak dmp mdf ldf trn) — no sampler reads these. `.sql` is
 *     deliberately ABSENT: it sniffs as text and IS scanned as prose.
 *   - VM and disk images (iso vmdk vhd vhdx ova)
 *   - media (mp4 mov avi mp3 wav)
 *   - images (png jpg jpeg gif bmp tif tiff) — counted today but never sampled.
 *     ⚠ Remove images from this default when OCR support ships.
 *
 * zip and gz are deliberately absent: those legitimately carry documents.
 *
 * This is a seed, not policy — it is stored on the source like any typed value, and the
 * user can edit or clear it.
 */
export const DEFAULT_FILE_EXCLUDE_FILTER = [
  '*.jar', '*.war', '*.ear', '*.class', '*.dll', '*.so', '*.pdb', '*.exe', '*.msi',
  '*.bak', '*.dmp', '*.mdf', '*.ldf', '*.trn',
  '*.iso', '*.vmdk', '*.vhd', '*.vhdx', '*.ova',
  '*.mp4', '*.mov', '*.avi', '*.mp3', '*.wav',
  '*.png', '*.jpg', '*.jpeg', '*.gif', '*.bmp', '*.tif', '*.tiff',
].join(', ');

/**
 * Does `value` read as a comma-separated glob list rather than a regex?
 *
 * Mirrors `FileNameGlobs.toRegexOrNull` in c2-discover-api — same trigger (a separator or
 * a wildcard) and same veto (any regex-only metacharacter in a token, which is what keeps
 * `\.(jar|war){1,2}$` a regex despite its comma). Kept deliberately small and duplicated:
 * this side only decides whether to show the advisory "doesn't look like a valid regular
 * expression" warning, and the worker remains the authority on what actually matches.
 */
export function looksLikeGlobList(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return false;
  // A `/` counts as a trigger too: a folder pattern like `**/build/**` has no comma and is
  // an invalid regex, so without this the field would warn on a perfectly good exclusion.
  if (!/[,;\n*?/]/.test(trimmed)) return false;
  const tokens = trimmed.split(/[,;\n]/).map((t) => t.trim()).filter((t) => t !== '');
  // No tokens (e.g. ",,,") is not a glob list — the worker hands that back to the regex
  // compiler too, so the advisory must agree.
  return tokens.length > 0 && tokens.every((t) => !/[\\()[\]{}|^$+]/.test(t));
}

export function toRequestDto(row) {
  const c = row || {};
  if (c.type === FILE_SOURCE_TYPE) {
    // File source — vfsScheme is the platform (LOCAL/S3/AZURE_BLOB/SFTP). Per-scheme
    // credentials and rootPath ride in config.vfsCredentials / config.rootPath, mirroring
    // the worker's existing scan/start contract. New file sources default to Folder Search
    // (FILE_GENERIC_FOLDER): the user no longer picks a type — the extension is the type, and
    // generic discovery walks the folder + subfolders, skipping what it can't read. Editing an
    // existing typed row preserves its saved datasourceType via toUiDataSource's `fileType`.
    const datasourceType = c.fileType || 'FILE_GENERIC_FOLDER';
    const vfsCredentials = buildFileVfsCredentials(c);
    if (datasourceType === 'FILE_DELIMITED_FOLDER' && c.delimiter != null && c.delimiter !== '') {
      vfsCredentials.delimiter = c.delimiter;
    }
    const config = {};
    if (c.rootPath != null && c.rootPath !== '') config.rootPath = c.rootPath;
    if (Object.keys(vfsCredentials).length) config.vfsCredentials = vfsCredentials;
    // Discovery scope controls (2026-07). recurse is written explicitly (not omitted when
    // true) so an update PUT can never leave a stale false behind; fileNameFilter is a
    // Java regex against the leaf file name — blank means "no filter" and is omitted.
    config.recurse = c.recurse !== false;
    // Both filters follow the same rule: blank or '*' means "not set" and the key is
    // omitted. The worker shares one compile routine for them, so the two can't disagree
    // on what unset means.
    const fileNameFilter = typeof c.fileNameFilter === 'string' ? c.fileNameFilter.trim() : '';
    if (fileNameFilter && fileNameFilter !== '*') config.fileNameFilter = fileNameFilter;
    const fileNameExcludeFilter = typeof c.fileNameExcludeFilter === 'string'
      ? c.fileNameExcludeFilter.trim() : '';
    if (fileNameExcludeFilter && fileNameExcludeFilter !== '*') {
      config.fileNameExcludeFilter = fileNameExcludeFilter;
    }
    // "Discover everything" opt-in (Folder Search only): persisted source setting the
    // platform stamps onto each discover job at start. Written explicitly (like recurse)
    // so un-ticking it on an update actually clears the stored value.
    if (datasourceType === 'FILE_GENERIC_FOLDER') {
      config.autoDiscoverCandidates = c.autoDiscoverCandidates === true;
    }
    const fileDescription = typeof c.description === 'string' ? c.description.trim() : '';
    if (fileDescription) config.description = fileDescription;
    // engine carries the VFS scheme in lowercase so the data sources table's row
    // icon (driven by engine) renders the right platform glyph (s3 / azure_blob /
    // sftp / local). The UI's internal "is this a file source?" branching keys off
    // `connection.type === FILE_SOURCE_TYPE` instead of engine.
    return {
      datasourceType,
      name: c.name,
      engine: (c.vfsScheme || 'LOCAL').toLowerCase(),
      vfsScheme: c.vfsScheme,
      config,
      readOnly: c.readOnly === true,
      ...preferredProvidersDto(c),
    };
  }
  const datasourceType = uiTypeToDatasourceType(c.type);
  const engine = c.type || inferEngineFromDatasourceType(datasourceType);
  const config = buildDataSourceConfig(c);
  const dto = {
    datasourceType,
    name: c.name,
    engine,
    config: Object.keys(config).length ? config : {},
    readOnly: c.readOnly === true,
    ...preferredProvidersDto(c),
  };
  // authMode + cloudAccountId are TOP-LEVEL on the request DTO (siblings of config), not
  // inside it. Send authMode explicitly (null == PASSWORD on the backend, so an explicit
  // 'PASSWORD' is safe and keeps the wire value unambiguous). Only attach cloudAccountId for
  // token modes and only when set — an empty string would fail the backend's UUID bind, and a
  // stale id on a PASSWORD source is nulled server-side anyway.
  // SAAS authenticates with OAuth (config.authFlow), so it declares OAUTH rather than
  // misreporting PASSWORD. The platform still accepts PASSWORD for rows created before the
  // DataSourceAuthMode enum gained OAUTH.
  const authMode = c.authMode || (isSaasSource(c) ? 'OAUTH' : 'PASSWORD');
  dto.authMode = authMode;
  if (authMode !== 'PASSWORD' && c.cloudAccountId) dto.cloudAccountId = c.cloudAccountId;
  // FIELDS mode: never ship a stale jdbcUrl that the user can't see. JDBC_URL mode: send the URL.
  // Legacy `engine === 'jdbc'` rows still use host as the URL string (no mode field).
  const sendJdbcUrl = c._connectionMode !== 'FIELDS';
  if (sendJdbcUrl && c.jdbcUrl) dto.jdbcUrl = c.jdbcUrl;
  if (engine === 'jdbc' && c.host) dto.jdbcUrl = c.host;
  return dto;
}

function readConfig(dto) {
  if (!dto) return {};
  const cfg = dto.config ?? dto.connectionDetails;
  return cfg && typeof cfg === 'object' ? cfg : {};
}

/**
 * API row → in-memory object for forms and tables (still exposed as `connection` in much UI state).
 *
 * `_connectionMode` is a UI-only hint that lets the editor open in the right mode (host/port
 * fields vs. JDBC URL string) without forcing the user to flip the toggle on every edit. It is
 * not persisted on the API side and is filtered out by {@link toRequestDto}.
 */
export function toUiDataSource(dto) {
  if (!dto) return {};
  const d = readConfig(dto);
  if (isFileDatasourceType(dto.datasourceType) && dto.datasourceType !== 'FILE_FOLDER') {
    // New unified file editor — flatten vfsCredentials into per-scheme fields the form
    // binds to directly, and surface fileType so the file-type dropdown can resolve.
    // FILE_FOLDER is the legacy s3/sftp shape; let it fall through to the engine-based
    // mapping below so existing rows keep editing as they did before.
    const vc = (d.vfsCredentials && typeof d.vfsCredentials === 'object') ? d.vfsCredentials : {};
    return {
      id: dto.id,
      name: dto.name,
      type: FILE_SOURCE_TYPE,
      // Drives the type dropdown's selectedOption lookup on edit (file_local etc.).
      customDatabaseType: vfsSchemeToTypeOption(dto.vfsScheme),
      datasourceType: dto.datasourceType,
      fileType: dto.datasourceType,
      vfsScheme: dto.vfsScheme ?? null,
      rootPath: d.rootPath ?? '',
      vfsCredentials: vc,
      // Flattened credential fields, per scheme — the editor binds these directly so
      // the user sees populated fields on edit. toRequestDto reassembles vfsCredentials
      // on save.
      vfsHost: vc.host ?? '',
      vfsPort: vc.port ?? '',
      vfsUsername: vc.username ?? '',
      vfsPassword: isRedactedSecret(vc.password) ? '' : (vc.password ?? ''),
      region: vc.region ?? '',
      accessKeyId: vc.accessKeyId ?? '',
      secretAccessKey: isRedactedSecret(vc.secretAccessKey) ? '' : (vc.secretAccessKey ?? ''),
      endpointUrl: vc.endpointUrl ?? '',
      account: vc.account ?? '',
      container: vc.container ?? '',
      accountKey: isRedactedSecret(vc.accountKey) ? '' : (vc.accountKey ?? ''),
      sasToken: isRedactedSecret(vc.sasToken) ? '' : (vc.sasToken ?? ''),
      connectionString: isRedactedSecret(vc.connectionString) ? '' : (vc.connectionString ?? ''),
      tenantId: vc.tenantId ?? '',
      clientId: vc.clientId ?? '',
      clientSecret: isRedactedSecret(vc.clientSecret) ? '' : (vc.clientSecret ?? ''),
      certificatePem: vc.certificatePem ?? '',
      certificatePrivateKey: isRedactedSecret(vc.certificatePrivateKey) ? '' : (vc.certificatePrivateKey ?? ''),
      delimiter: vc.delimiter ?? '',
      // Discovery scope controls — absent recurse means true (pre-feature rows).
      recurse: d.recurse !== false,
      fileNameFilter: d.fileNameFilter ?? '',
      fileNameExcludeFilter: d.fileNameExcludeFilter ?? '',
      autoDiscoverCandidates: d.autoDiscoverCandidates === true,
      description: typeof d.description === 'string' ? d.description : '',
      creationTime: dto.createdAt,
      lastUpdated: dto.updatedAt,
      registeredServiceId: dto.registeredServiceId || null,
      registeredServiceName: dto.registeredServiceName || null,
      externalId: dto.externalId || null,
      importedAt: dto.importedAt || null,
      managedBy: dto.managedBy || (d && d.managedBy) || null,
      needsCredentials: dto.needsCredentials === true,
      readOnly: dto.readOnly === true,
      preferredDiscoverProviderId: dto.preferredDiscoverProviderId || null,
      parentDataSourceId: d.parentDataSourceId != null ? Number(d.parentDataSourceId) : null,
      // Lifecycle owner (e.g. tdm-staging:<id>, tdm-subset:<id>): listed, but edited through that feature.
      ownerRef: dto.ownerRef || null,
      lastTestAt: dto.lastTestAt ?? null,
      lastTestSuccess: dto.lastTestSuccess ?? null,
      healthcareTag: dto.healthcareTag ?? null,
      healthcareTagSource: dto.healthcareTagSource ?? null,
      // Key/value tags (always carries `landscape`: sensitive | enterprise | both).
      tags: dto.tags && typeof dto.tags === 'object' ? dto.tags : {},
      tableData: dto.id != null ? { id: dto.id } : undefined,
    };
  }
  const engine = dto.engine || inferEngineFromDatasourceType(dto.datasourceType);
  const hostForUi = engine === 'jdbc' && dto.jdbcUrl ? dto.jdbcUrl : d.host;
  const hasJdbcUrl = !!(dto.jdbcUrl || d.jdbcUrl);
  return {
    id: dto.id,
    name: dto.name,
    type: engine,
    // datasourceType is the top-level enum (RELATIONAL / FILE_*_FOLDER / NOSQL / FILE_FOLDER).
    // Distinct from `type` (engine string like "postgresql"); preserved here so callers like
    // RunDiscoveryButton can branch routing without re-fetching the DTO.
    datasourceType: dto.datasourceType ?? null,
    // File-source fields exposed for the run / wizard flows. Null/undefined for non-file rows.
    vfsScheme: dto.vfsScheme ?? null,
    rootPath: d.rootPath ?? null,
    vfsCredentials: d.vfsCredentials && typeof d.vfsCredentials === 'object' ? d.vfsCredentials : null,
    jdbcUrl: dto.jdbcUrl,
    host: hostForUi,
    port: d.port,
    database: d.database,
    schemaName: d.schema,
    user: d.username,
    // Never bind password inputs to API placeholders like "****" — controlled fields may not update reliably.
    password: isRedactedSecret(d.password) ? '' : (d.password ?? ''),
    // Auth mode round-trip. authMode/cloudAccountId are top-level on the response DTO; region
    // lives inside config. Default authMode to PASSWORD so pre-feature rows (null auth_mode)
    // render correctly. null cloudAccountId → the editor's "Default (platform identity)" option.
    authMode: dto.authMode || 'PASSWORD',
    cloudAccountId: dto.cloudAccountId || null,
    region: d.region ?? '',
    // DynamoDB round-trip (config keys; harmlessly empty for every other engine). The
    // secret is redaction-guarded like password/privateKey so a "****" placeholder never
    // binds to the editor field or gets sent back on save.
    accessKeyId: d.accessKeyId ?? '',
    secretAccessKey: isRedactedSecret(d.secretAccessKey) ? '' : (d.secretAccessKey ?? ''),
    endpointUrl: d.endpointUrl ?? '',
    table: d.table ?? '',
    // Salesforce (SAAS) round-trip. Secrets are redaction-guarded like password: a "****"
    // placeholder never binds to the editor and is never sent back (toRequestDto skips it,
    // the platform carries the stored value forward).
    loginUrl: d.loginUrl ?? '',
    authFlow: d.authFlow ?? '',
    consumerKey: d.consumerKey ?? '',
    username: d.username ?? '',
    privateKeyPem: isRedactedSecret(d.privateKeyPem) ? '' : (d.privateKeyPem ?? ''),
    clientSecret: isRedactedSecret(d.clientSecret) ? '' : (d.clientSecret ?? ''),
    apiVersion: d.apiVersion ?? '',
    audience: d.audience ?? '',
    advanced: (d.advanced && typeof d.advanced === 'object') ? { ...d.advanced } : undefined,
    selectedObjects: Array.isArray(d.selectedObjects) ? d.selectedObjects.map(String) : undefined,
    warehouse: d.warehouse,
    path: d.path,
    privateKey: isRedactedSecret(d.privateKey) ? '' : (d.privateKey ?? ''),
    protocol: d.protocol,
    peering: d.peering,
    // Round-trip SQL Server TLS toggles. Booleans only — leave undefined when the API didn't
    // persist them so the editor's "default to true" branch still kicks in for legacy rows.
    encrypt: typeof d.encrypt === 'boolean' ? d.encrypt : undefined,
    trustServerCertificate: typeof d.trustServerCertificate === 'boolean' ? d.trustServerCertificate : undefined,
    // Stored as an object map; render back to KEY=VALUE lines for the textarea. Legacy
    // string values (pre-object rows) are normalized to the same line-oriented shape.
    connectionProperties: connectionPropertiesToText(d.connectionProperties),
    customDatabaseType: d.customDatabaseType ?? null,
    // Free-text description (connection_details.description) — '' when the source has none.
    description: typeof d.description === 'string' ? d.description : '',
    // Auto-expand the Advanced section on edit when there's content saved there. Salesforce keeps
    // its advanced state in `audience` / `advanced` rather than connectionProperties, so without
    // the second clause an org with a non-default audience would open looking untouched.
    _showAdvanced: !!(
      (d.connectionProperties
        && (typeof d.connectionProperties !== 'object' || Object.keys(d.connectionProperties).length > 0))
      || (d.audience && String(d.audience).trim() !== '')
      || (d.advanced && typeof d.advanced === 'object' && Object.keys(d.advanced).length > 0)
    ),
    _connectionMode: hasJdbcUrl ? 'JDBC_URL' : 'FIELDS',
    creationTime: dto.createdAt,
    lastUpdated: dto.updatedAt,
    registeredServiceId: dto.registeredServiceId || null,
    registeredServiceName: dto.registeredServiceName || null,
    externalId: dto.externalId || null,
    importedAt: dto.importedAt || null,
    managedBy: dto.managedBy || (d && d.managedBy) || null,
    // True when the row was imported (e.g. from a JSON export) without a usable password.
    // Cleared on the API side once the user opens the editor and saves a real secret.
    // The UI uses this to render a "Local (imported)" pill and to hide the row from
    // discover/mask/encrypt pickers, mirroring the integration-unadopted treatment.
    needsCredentials: dto.needsCredentials === true,
    readOnly: dto.readOnly === true,
    preferredDiscoverProviderId: dto.preferredDiscoverProviderId || null,
    parentDataSourceId: d.parentDataSourceId != null ? Number(d.parentDataSourceId) : null,
    // Lifecycle owner (e.g. tdm-staging:<id>, tdm-subset:<id>): listed, but edited through that feature.
    ownerRef: dto.ownerRef || null,
    // Tri-state last connection-test verdict (null = never tested). The pipeline board
    // buckets saved sources on this: untested/failed stay "Surfaced".
    lastTestAt: dto.lastTestAt ?? null,
    lastTestSuccess: dto.lastTestSuccess ?? null,
    // Healthcare (HIPAA) classification, auto-detected server-side. `healthcareTag` is a
    // tri-state (true/false/null); `healthcareTagSource` names the signals that set it
    // (e.g. "COMMERCIAL_MATCH,ENTITY_FINDINGS"). Drives the safe-harbor map fetch + badge.
    healthcareTag: dto.healthcareTag ?? null,
    healthcareTagSource: dto.healthcareTagSource ?? null,
    // Key/value tags (always carries `landscape`: sensitive | enterprise | both).
    tags: dto.tags && typeof dto.tags === 'object' ? dto.tags : {},
    tableData: dto.id != null ? { id: dto.id } : undefined,
  };
}

/** @deprecated use toUiDataSource */
export const toUiConnection = toUiDataSource;

/**
 * Returns true if this data source originated from a 3rd-party integration import (e.g. Delphix),
 * regardless of whether it has since been adopted. Provenance predicate — use it for badges and
 * origin labels; use {@link isImportedUnadopted} for the actionability gate.
 */
export function isIntegrationImported(connection) {
  return !!(connection && connection.registeredServiceId);
}

/**
 * Returns true if this data source was imported from an integration but has NOT
 * been adopted by C2 (i.e. the user has not supplied credentials for it yet).
 * Such rows are read-only catalog entries — we don't have the password, so any
 * discover/mask/encrypt run against them is guaranteed to fail.
 */
export function isImportedUnadopted(connection) {
  if (!connection) return false;
  if (!connection.registeredServiceId) return false;
  // Adoption is fundamentally "the platform holds usable credentials for this row" — the
  // server's needsCredentials flag. The managedBy marker is the explicit adoption stamp,
  // but rows saved under an older build could lose it on update; a row with stored
  // credentials must never be treated as unadopted (it would vanish from the inventory,
  // masking and encryption pages while being perfectly runnable).
  return connection.managedBy !== 'c2' && connection.needsCredentials === true;
}

/**
 * Returns true if this data source is awaiting user-supplied credentials. Two cases:
 *   - integration-imported and not yet adopted (covered by {@link isImportedUnadopted})
 *   - native row created without a password (e.g. JSON-export re-import, server flag
 *     {@code needsCredentials})
 * In either case the platform has nothing to authenticate with, so the row should be
 * hidden from discover / mask / encrypt pickers until the user opens the editor and
 * saves a real secret.
 */
export function needsCredentials(connection) {
  if (!connection) return false;
  if (connection.needsCredentials === true) return true;
  return isImportedUnadopted(connection);
}

/**
 * Drops data sources awaiting credentials (integration-unadopted *or* JSON-import
 * incomplete) from a list. Use this anywhere a connection is about to be acted on
 * (discover / mask / encrypt dropdowns and tables) so users aren't offered rows the
 * platform can't authenticate against.
 */
export function filterActionableConnections(connections) {
  if (!Array.isArray(connections)) return [];
  return connections.filter((c) => !needsCredentials(c));
}

/**
 * A row owned by a platform feature (`ownerRef` set — today a Test Data Management staging
 * source `tdm-staging:<id>` or a subset's raw view `tdm-subset:<id>`). It is listed like any
 * other source, but its settings live on the owning feature: the API refuses PUT / DELETE / test,
 * and the landscapes do not show it (tag landscape=none).
 */
export const isPlatformManaged = (c) => Boolean(c && c.ownerRef);

/**
 * Tagged `landscape=none` — SYSTEM-set on TDM staging sources and raw views, settable on any
 * source through the tag endpoint. Such a row is left off the Data Sources list as well as the
 * Enterprise Data Landscape and the Data Privacy Workflow; the Secure landscape, the pickers and
 * every other reader of the connections array still see it.
 */
export const isHiddenFromSourceList = (c) =>
  Boolean(c && c.tags) && String(c.tags.landscape || '').toLowerCase() === 'none';

/** 'staging' | 'raw-view' | null — which TDM object a platform-managed row stands for. */
export const platformOwnerKind = (c) => {
  const ref = String((c && c.ownerRef) || '');
  if (ref.startsWith('tdm-staging:')) return 'staging';
  if (ref.startsWith('tdm-subset:')) return 'raw-view';
  return ref ? 'other' : null;
};

/** The numeric id inside an ownerRef (`tdm-subset:12` → 12), or null. */
export const platformOwnerId = (c) => {
  const m = String((c && c.ownerRef) || '').match(/:(\d+)$/);
  return m ? Number(m[1]) : null;
};

/**
 * Connections a user may start a job on from a picker: actionable AND not platform-managed. A TDM
 * raw view is masked by its subset run, never by hand; the Inventory Manager deliberately keeps
 * platform-managed rows (their inventories are editable) — it uses filterActionableConnections.
 */
export function filterJobStartable(connections) {
  return filterActionableConnections(connections).filter((c) => !isPlatformManaged(c));
}

/**
 * DataSourceTestResponseDTO → test panel + table fields.
 */
export function testDtoToUiSummary(dto, dataSourceName) {
  if (!dto) return {};
  const tested = dto.tested === true;
  const success = dto.success === true;
  let status;
  if (tested && success) status = 'SUCCEEDED';
  else if (tested && !success) status = 'FAILED';
  const ts = dto.testedAt ? Date.parse(dto.testedAt) : null;
  const meta = dto.meta || {};
  return {
    dataSource: dataSourceName,
    connection: dataSourceName,
    status,
    statusMessage: dto.error || dto.message || '',
    message: dto.error || dto.message,
    timestamp: Number.isFinite(ts) ? ts : undefined,
    lastTested: Number.isFinite(ts) ? ts : undefined,
    db_version: meta.databaseProductVersion,
    databases: meta.schemas,
    schemas: meta.schemas,
    // Engine-internal subset of `schemas` (worker-classified). Listed, but never offered a
    // per-schema "Create source" action. Absent on verdicts stored before classification.
    systemSchemas: Array.isArray(meta.systemSchemas) ? meta.systemSchemas : [],
    jobRunState: tested && !success ? 'FAILED' : undefined,
  };
}

export async function listDataSources() {
  const rows = await API.get(EP, BASE);
  return Array.isArray(rows) ? rows : [];
}

export async function getDataSourceById(id) {
  return API.get(EP, `${BASE}/${encodeURIComponent(id)}`);
}

export async function createDataSource(body) {
  return API.post(EP, BASE, { body });
}

export async function updateDataSource(id, body) {
  return API.put(EP, `${BASE}/${encodeURIComponent(id)}`, { body });
}

export async function deleteDataSourceById(id) {
  return API.del(EP, `${BASE}/${encodeURIComponent(id)}`);
}

export async function getLastDataSourceTest(id) {
  return API.get(EP, `${BASE}/${encodeURIComponent(id)}/test`);
}

export async function runDataSourceTest(id) {
  return API.post(EP, `${BASE}/${encodeURIComponent(id)}/test`);
}

/**
 * Start a discovery run for an existing file data source. Hits the platform-api's
 * id-based endpoint, which loads the source, reads its rootPath/vfsScheme/credentials,
 * then orchestrates the worker scan + start. Returns {dataSourceId, runId}.
 *
 * Replaces the legacy "describe a path + type" flow that tried to find-or-create the
 * data source server-side. RELATIONAL sources are not supported here — use the JDBC
 * discover button for those.
 */
export async function discoverFromDataSource(id) {
  return API.post(EP, `${BASE}/${encodeURIComponent(id)}/discover`);
}

/**
 * GET /api/datasources/{id}/delete-impact — returns counts of associated orchestration_jobs
 * rows that would be cascade-deleted, plus a best-effort derived encryption status.
 * Used by the pre-delete confirmation modal so users see an accurate blast radius.
 */
export async function getDeleteImpact(id) {
  return API.get(EP, `${BASE}/${encodeURIComponent(id)}/delete-impact`);
}

/** GET /api/datasources/{id}/tags — key/value tags; always includes `landscape`. */
export async function getDataSourceTags(id) {
  const tags = await API.get(EP, `${BASE}/${encodeURIComponent(id)}/tags`);
  return tags && typeof tags === 'object' ? tags : {};
}

/**
 * PUT /api/datasources/{id}/tags/{key} — sets one tag as an explicit user value and returns
 * the full tag map. A single-row upsert server-side: it never goes through the data-source
 * PUT, so it cannot race the editor or the auto connection test. For `landscape` the value
 * must be sensitive | enterprise | both — nothing reads it any more (every source is on both
 * landscapes since 2026-09-24); the endpoint stays as the generic tag facility.
 */
export async function setDataSourceTag(id, key, value) {
  const tags = await API.put(EP, `${BASE}/${encodeURIComponent(id)}/tags/${encodeURIComponent(key)}`, {
    body: { value },
  });
  return tags && typeof tags === 'object' ? tags : {};
}

export async function mergeLastTestIntoUiRow(uiRow) {
  const id = getDataSourceNumericId(uiRow);
  if (id == null) return uiRow;
  try {
    const testDto = await getLastDataSourceTest(id);
    const extra = testDtoToUiSummary(testDto, uiRow.name);
    return {
      ...uiRow,
      ...extra,
      connectionTest: testDto.tested ? extra : undefined,
    };
  } catch (e) {
    return uiRow;
  }
}

export async function listDataSourcesWithLastTest() {
  const rows = await listDataSources();
  const ui = rows.map(toUiDataSource);
  return Promise.all(ui.map((c) => mergeLastTestIntoUiRow(c)));
}

/** @deprecated */
export const listConnections = listDataSources;
export const listConnectionsWithLastTest = listDataSourcesWithLastTest;
export const getConnectionById = getDataSourceById;
export const createConnection = createDataSource;
export const updateConnection = updateDataSource;
export const deleteConnectionById = deleteDataSourceById;
export const getLastConnectionTest = getLastDataSourceTest;
export const runConnectionTest = runDataSourceTest;
export const mergeLastTestIntoUiConnection = mergeLastTestIntoUiRow;

/**
 * Parse uploaded JSON: array, or { connections | dataSources: [...] }, or single object.
 */
export function parseDataSourcesImportJson(parsed) {
  if (parsed == null) return [];
  if (Array.isArray(parsed)) return parsed;
  if (Array.isArray(parsed.connections)) return parsed.connections;
  if (Array.isArray(parsed.dataSources)) return parsed.dataSources;
  if (typeof parsed === 'object') return [parsed];
  return [];
}

/**
 * Raw JSON row → UI shape for {@link toRequestDto} / create API (strips server-only fields).
 *
 * Also used by {@link connectionsForExportJson} to shape rows on the way OUT, so any field that
 * needs to survive a backup/restore round-trip must be passed through here. New persisted fields
 * (customDatabaseType for the registered-driver picker, connectionProperties for per-connection
 * JDBC props, the SQL Server TLS booleans) are explicitly forwarded for that reason.
 */
export function normalizeImportRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const name = String(raw.name || '').trim();
  if (!name) return null;

  // File sources round-trip through a different shape than JDBC sources: they carry
  // vfsScheme + a FILE_* datasourceType and their credentials live in per-scheme fields,
  // not host/user/password. Detect them BEFORE the JDBC default kicks in — otherwise a
  // file row with no `type`/`engine` would be coerced to 'mysql' and lose every VFS field.
  // `fileType` is the import/export field name (mirrors toUiDataSource); `datasourceType`
  // is accepted too so a raw DTO-shaped export still imports.
  const rawFileType = raw.fileType || (isFileDatasourceType(raw.datasourceType) ? raw.datasourceType : null);
  if (raw.type === FILE_SOURCE_TYPE || raw.vfsScheme || rawFileType) {
    const fileRow = {
      name,
      type: FILE_SOURCE_TYPE,
      fileType: rawFileType || 'FILE_GENERIC_FOLDER',
      vfsScheme: raw.vfsScheme || 'LOCAL',
      rootPath: raw.rootPath ?? '',
      _connectionMode: 'FILE',
    };
    // SFTP
    if (raw.vfsHost != null) fileRow.vfsHost = raw.vfsHost;
    if (raw.vfsPort != null) fileRow.vfsPort = raw.vfsPort;
    if (raw.vfsUsername != null) fileRow.vfsUsername = raw.vfsUsername;
    if (raw.vfsPassword != null) fileRow.vfsPassword = raw.vfsPassword;
    // S3
    if (raw.region != null) fileRow.region = raw.region;
    if (raw.accessKeyId != null) fileRow.accessKeyId = raw.accessKeyId;
    if (raw.secretAccessKey != null) fileRow.secretAccessKey = raw.secretAccessKey;
    if (raw.endpointUrl != null) fileRow.endpointUrl = raw.endpointUrl;
    // Azure Blob
    if (raw.account != null) fileRow.account = raw.account;
    if (raw.container != null) fileRow.container = raw.container;
    if (raw.accountKey != null) fileRow.accountKey = raw.accountKey;
    if (raw.sasToken != null) fileRow.sasToken = raw.sasToken;
    if (raw.connectionString != null) fileRow.connectionString = raw.connectionString;
    // SharePoint
    if (raw.tenantId != null) fileRow.tenantId = raw.tenantId;
    if (raw.clientId != null) fileRow.clientId = raw.clientId;
    if (raw.clientSecret != null) fileRow.clientSecret = raw.clientSecret;
    if (raw.certificatePem != null) fileRow.certificatePem = raw.certificatePem;
    if (raw.certificatePrivateKey != null) fileRow.certificatePrivateKey = raw.certificatePrivateKey;
    // Delimited
    if (raw.delimiter != null) fileRow.delimiter = raw.delimiter;
    return fileRow;
  }

  const type = String(raw.type || raw.engine || 'mysql').trim() || 'mysql';
  const row = {
    name,
    type,
    jdbcUrl: raw.jdbcUrl,
    host: raw.host,
    port: raw.port,
    database: raw.database,
    schemaName: raw.schemaName != null ? raw.schemaName : raw.schema,
    user: raw.user != null ? raw.user : raw.username,
    password: raw.password,
    warehouse: raw.warehouse,
    path: raw.path,
    privateKey: raw.privateKey,
    protocol: raw.protocol,
    peering: raw.peering,
    customDatabaseType: raw.customDatabaseType ?? null,
    // Normalize to KEY=VALUE textarea text regardless of the source shape: UI rows carry text,
    // new backups carry an object map, legacy backups carry a delimited string. toRequestDto
    // re-objectifies it on the way back out.
    connectionProperties: connectionPropertiesToText(raw.connectionProperties),
    // Auth-mode fields survive backup/restore. None are secrets, so they also flow through
    // dataSourceDetailsForExport (which strips every field in EXPORT_SECRET_FIELDS). Keep authMode
    // null when absent — toRequestDto defaults null → 'PASSWORD' on the way back out.
    authMode: raw.authMode ?? null,
    cloudAccountId: raw.cloudAccountId ?? null,
    region: raw.region ?? null,
    _connectionMode: raw.jdbcUrl ? 'JDBC_URL' : 'FIELDS',
  };
  // DynamoDB import fields. buildDataSourceConfig's dynamo branch reads exactly these;
  // for other engines they're inert (never shipped). secretAccessKey rides IN on import
  // like password does — exports still strip it via EXPORT_SECRET_FIELDS.
  if (raw.table != null) row.table = raw.table;
  // Salesforce import fields (buildDataSourceConfig's salesforce branch reads exactly these).
  // The secrets ride IN on import like password does; exports strip them via EXPORT_SECRET_FIELDS.
  if (raw.loginUrl != null) row.loginUrl = raw.loginUrl;
  if (raw.authFlow != null) row.authFlow = raw.authFlow;
  if (raw.consumerKey != null) row.consumerKey = raw.consumerKey;
  if (raw.username != null) row.username = raw.username;
  if (raw.privateKeyPem != null) row.privateKeyPem = raw.privateKeyPem;
  if (raw.clientSecret != null) row.clientSecret = raw.clientSecret;
  if (raw.apiVersion != null) row.apiVersion = raw.apiVersion;
  if (raw.accessKeyId != null) row.accessKeyId = raw.accessKeyId;
  if (raw.secretAccessKey != null) row.secretAccessKey = raw.secretAccessKey;
  if (raw.endpointUrl != null) row.endpointUrl = raw.endpointUrl;
  if (typeof raw.encrypt === 'boolean') row.encrypt = raw.encrypt;
  if (typeof raw.trustServerCertificate === 'boolean') {
    row.trustServerCertificate = raw.trustServerCertificate;
  }
  return row;
}

// Every secret field across both source shapes: JDBC (password/privateKey) and the
// per-scheme file credentials (SFTP/S3/Azure Blob). Secrets must NEVER leave the app in
// an export — re-import treats a missing secret as "needs credentials", which is the
// intended, safe round-trip. Keep this list in sync with buildFileVfsCredentials /
// buildDataSourceConfig whenever a new credential field is added.
const EXPORT_SECRET_FIELDS = [
  'password',
  'privateKey',
  'vfsPassword',
  'secretAccessKey',
  'accountKey',
  'sasToken',
  'connectionString',
  'clientSecret',
  'certificatePrivateKey',
  'privateKeyPem',
];

/** Strip every secret field from an import-shaped row (in place-safe; returns a new object). */
function stripExportSecrets(row) {
  if (!row) return row;
  const out = { ...row };
  for (const f of EXPORT_SECRET_FIELDS) delete out[f];
  return out;
}

/** Rows safe to export (portable backup / re-import) — secrets stripped. */
export function connectionsForExportJson(connections) {
  return (connections || [])
    .map((c) => normalizeImportRow(c))
    .filter(Boolean)
    .map(stripExportSecrets);
}

/**
 * UI data source row for discovery export / sharing — same shape as import rows, without secrets.
 * @param {object} uiRow
 * @returns {object|null}
 */
export function dataSourceDetailsForExport(uiRow) {
  const row = normalizeImportRow(uiRow);
  if (!row) return null;
  return stripExportSecrets(row);
}
